import { createSelector } from '@ngrx/store';
import { surveyCreationFeature } from '../survey-creation.feature';
import { DEFAULT_CHILD_AUDIENCE_STATE } from '../survey-creation-state.constants';
import { resolveActiveChildKey } from './utils/audience-children.helper';

const { selectAudience } = surveyCreationFeature;

/**
 * The child currently being worked on — see `resolveActiveChildKey`. Falls
 * back to a fresh default child so downstream selectors never have to
 * null-check a missing entry.
 */
const selectActiveChild = createSelector(selectAudience, (state) => {
  const key = resolveActiveChildKey(state);
  return state.children[key] ?? { ...DEFAULT_CHILD_AUDIENCE_STATE, key };
});

export const selectSurveyId = createSelector(
  selectActiveChild,
  ({ surveyId }) => surveyId,
);

export const selectSurvey = createSelector(
  selectActiveChild,
  ({ survey }) => survey,
);

export const selectMetadataForm = createSelector(
  selectActiveChild,
  ({ metadata }) => metadata,
);

export const selectSurveyClickBalancing = createSelector(
  selectMetadataForm,
  ({ value }) => !!value?.clickBalancing,
);

export const selectIsMetadataValid = createSelector(
  selectMetadataForm,
  ({ valid }) => valid,
);

export const selectQualificationsForm = createSelector(
  selectActiveChild,
  ({ qualifications }) => qualifications,
);

export const selectQuotasForm = createSelector(
  selectActiveChild,
  ({ quotas }) => quotas,
);

export const selectSurveyFormState = createSelector(
  selectMetadataForm,
  selectQualificationsForm,
  selectQuotasForm,
  (metadata, qualifications, quotas) => ({ metadata, qualifications, quotas }),
);

/** The applied Feasibility filters (exclusions/inclusions) */
export const selectAdvancedFeasibilityFilters = createSelector(
  selectActiveChild,
  ({ advancedFeasibilityFilters }) => advancedFeasibilityFilters,
);

export const selectError = createSelector(
  selectActiveChild,
  ({ error }) => error,
);

export const selectLocationFileUpload = createSelector(
  selectActiveChild,
  ({ locationFileUpload }) => locationFileUpload,
);

export const selectLinkedTagIds = createSelector(
  selectActiveChild,
  ({ tagLinks }) => tagLinks.linkedTagIds,
);

export const selectLinkTagsError = createSelector(
  selectActiveChild,
  ({ tagLinks }) => tagLinks.linkTagsError,
);

export const selectSuppliersMetaDataBlendId = createSelector(
  selectActiveChild,
  ({ suppliersMetaData }) => suppliersMetaData.blendId,
);

export const selectIsDirty = createSelector(
  selectActiveChild,
  ({ metadata, qualifications, quotas }) =>
    metadata.isDirty || qualifications.isDirty || quotas.isDirty,
);

export const selectIsPreDraft = createSelector(
  selectAudience,
  (state) => !state.parentId && Object.keys(state.children).length > 1,
);

export const selectChildren = createSelector(
  selectAudience,
  ({ children }) => children,
);

export const selectIsDrawablePreDraft = createSelector(
  selectIsMetadataValid,
  selectIsPreDraft,
  (isMetadataValid, isPreDraft) => isMetadataValid && isPreDraft,
);
